import { printHTML, downloadWord } from '../utils/printHelper';
import { useEffect, useState, useMemo } from 'react';
import { api } from '../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Pencil, Trash, Eye, Printer, FileDoc, MagnifyingGlass } from '@phosphor-icons/react';
import { DateInput } from '../components/DateInput';
import { formatDatePrint } from '../utils/dateFormat';
import { confirmDialog } from '@/components/ConfirmDialog';

const DJELATNOSTI = {
  gazdinstva: { label: 'Gazdinstva', options: [
    'Uzgoj živine i pakovanje jaja, kokoški, prepelica',
    'Proizvodnja, punjenje i/ili pakovanje meda i pčelinjih proizvoda',
    'Proizvodnja sirovog mlijeka - za prodaju/preradu na sopstvenom gazdinstvu',
    'Proizvodnja sirovog mlijeka - radi predaje objektima za preradu mlijeka',
    'Sakupljanje sirovog mlijeka sa više gazdinstava',
  ]},
  sabirni_centri: { label: 'Sabirni centri za mlijeko', options: [] },
  skoljke: { label: 'Uzgajališta školjki', options: ['Morski', 'Slatkovodni'] },
  ribnjaci: { label: 'Ribnjaci / Uzgajališta ribe', options: [] },
  brod: { label: 'Brod (osim broda hladnjače i broda fabrike)', options: [] },
  puzevi: { label: 'Uzgajalište puževa', options: [] },
  ugostiteljstvo: { label: 'Ugostiteljstvo', options: [
    'Hoteli - hotel, mali hotel, boutique hotel, garni hotel, apart hotel, pansion, motel, hostel',
    'Resort, hotel&resort, turističko naselje, turistička vila, etno selo, eko lodge',
    'Kuća za iznajmljivanje, turistički apartman, soba za iznajmljivanje, gostionica, planinarski dom',
    'Restoran, picerija, konoba, catering objekt, brza hrana',
    'Caffe bar, kafana, kafić, kafeterija, disko klub, noćni klub, bistro, pivnica',
    'Pekara, poslastičarnica, kantina, pečenjara, ćevabdžinica, buregdžinica',
    'Institucionalne kuhinje (obrazovne, socijalne, zdravstvene)',
    'Ostalo',
  ]},
  trgovina: { label: 'Sektor trgovina', options: [
    'Trgovina na malo',
    'Veleprodaja i skladištenje hrane',
    'Apoteke i specijalizovane prodavnice',
    'Pijace / Tržnica',
    'Ostalo',
  ]},
  gazdinstvo_prodaja: { label: 'Proizvodnja i prodaja hrane na poljoprivrednom gazdinstvu', options: [] },
  ostalo: { label: 'Ostalo', options: [] },
};

const DOKUMENTACIJA = [
  'Rješenje/izvod iz Centralnog registra privrednih subjekata',
  'Dokaz o svojini (list nepokretnosti ili ugovor o zakupu)',
  'Skica objekta sa rasporedom prostorija i opreme',
  'Opis tehnoloških postupaka u proizvodnji/preradi/pakovanju',
  'Dozvola za marikulturu ili akvakulturu',
  'Dozvola za privredni ribolov',
  'Rješenje o korišćenju morskog dobra',
  'Dokaz o uplati administrativne takse',
];

const emptyForm = {
  client_id: '',
  subjekt_naziv: '', subjekt_adresa: '', subjekt_odgovorno_lice: '',
  subjekt_telefon: '', subjekt_email: '',
  objekat_naziv: '', objekat_adresa: '', objekat_povrsina: '',
  objekat_radna_mjesta: '', objekat_telefon: '', objekat_email: '',
  djelatnost_kategorija: '', djelatnost_opcije: [],
  dokumentacija: [],
  datum: new Date().toISOString().split('T')[0],
  napomena: '',
};

export default function RegistracijaHranePage() {
  const [entries, setEntries] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showDetail, setShowDetail] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [e, c] = await Promise.all([
        api.get('/zahtjevi-hrana'),
        api.get('/clients'),
      ]);
      setEntries(e.data);
      setClients(c.data);
    } catch { toast.error('Greska pri ucitavanju'); }
    finally { setLoading(false); }
  };

  const sortedClients = useMemo(() =>
    [...clients].sort((a, b) => (a.skraceni_naziv || a.naziv_firme || '').localeCompare(b.skraceni_naziv || b.naziv_firme || '')),
    [clients]);

  const getClientName = (id) => { const c = clients.find(c => c.id === id); return c ? (c.skraceni_naziv || c.naziv_firme) : ''; };

  const filtered = entries.filter(e => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (e.subjekt_naziv || '').toLowerCase().includes(s) || (e.objekat_naziv || '').toLowerCase().includes(s);
  });

  const handleClientChange = (cid) => {
    const c = clients.find(cl => cl.id === cid);
    if (c) {
      setForm(prev => ({
        ...prev, client_id: cid,
        subjekt_naziv: c.naziv_firme || c.skraceni_naziv || '',
        subjekt_adresa: `${c.adresa || ''} ${c.grad || ''}`.trim(),
        subjekt_odgovorno_lice: c.direktor || '',
        subjekt_telefon: c.telefon || '',
        subjekt_email: c.email || '',
      }));
    } else {
      setForm(prev => ({ ...prev, client_id: cid }));
    }
  };

  const handleSubmit = async () => {
    if (!form.subjekt_naziv.trim()) { toast.error('Unesite naziv subjekta'); return; }
    try {
      if (editingId) {
        await api.put(`/zahtjevi-hrana/${editingId}`, form);
        toast.success('Sacuvano');
      } else {
        await api.post('/zahtjevi-hrana', form);
        toast.success('Zahtjev kreiran');
      }
      setShowForm(false); setEditingId(null); setForm(emptyForm); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Greska'); }
  };

  const handleEdit = (e) => {
    setForm({
      client_id: e.client_id || '', subjekt_naziv: e.subjekt_naziv || '',
      subjekt_adresa: e.subjekt_adresa || '', subjekt_odgovorno_lice: e.subjekt_odgovorno_lice || '',
      subjekt_telefon: e.subjekt_telefon || '', subjekt_email: e.subjekt_email || '',
      objekat_naziv: e.objekat_naziv || '', objekat_adresa: e.objekat_adresa || '',
      objekat_povrsina: e.objekat_povrsina || '', objekat_radna_mjesta: e.objekat_radna_mjesta || '',
      objekat_telefon: e.objekat_telefon || '', objekat_email: e.objekat_email || '',
      djelatnost_kategorija: e.djelatnost_kategorija || '', djelatnost_opcije: e.djelatnost_opcije || [],
      dokumentacija: e.dokumentacija || [], datum: e.datum || '', napomena: e.napomena || '',
    });
    setEditingId(e.id); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!(await confirmDialog('Obrisati zahtjev?'))) return;
    try { await api.delete(`/zahtjevi-hrana/${id}`); toast.success('Obrisano'); fetchAll(); }
    catch { toast.error('Greska'); }
  };

  const toggleDjelatnostOpcija = (opt) => {
    setForm(prev => ({
      ...prev,
      djelatnost_opcije: prev.djelatnost_opcije.includes(opt)
        ? prev.djelatnost_opcije.filter(o => o !== opt)
        : [...prev.djelatnost_opcije, opt]
    }));
  };

  const toggleDokumentacija = (doc) => {
    setForm(prev => ({
      ...prev,
      dokumentacija: prev.dokumentacija.includes(doc)
        ? prev.dokumentacija.filter(d => d !== doc)
        : [...prev.dokumentacija, doc]
    }));
  };

  const buildHtml = (entry) => {
    const cb = (arr, val) => (arr || []).includes(val) ? '&#9745;' : '&#9744;';
    const doks = entry.dokumentacija || [];
    const opcije = entry.djelatnost_opcije || [];
    const kat = entry.djelatnost_kategorija || '';

    const client = clients.find(c => c.id === entry.client_id);
    const grad = client?.grad || '';

    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Obrazac 1</title>
<style>
@page { size: A4; margin: 10mm 12mm; }
* { margin:0; padding:0; box-sizing:border-box; }
body { font-family: Arial, Helvetica, sans-serif; font-size: 9pt; color: #000; line-height: 1.3; }
.hdr { text-align: right; font-weight: bold; font-size: 10pt; margin-bottom: 4px; }
.ttl { text-align: center; font-weight: bold; font-size: 11pt; margin: 3px 0 8px; }
table.main { width: 100%; border-collapse: collapse; }
table.main td, table.main th { border: 1px solid #000; padding: 3px 6px; font-size: 8.5pt; vertical-align: top; }
table.main th { background: #d9d9d9; text-align: left; font-size: 9pt; font-weight: bold; padding: 4px 6px; }
table.main td:first-child { width: 38%; }
table.main td.val { min-height: 20px; }
table.main td.cl { font-weight: bold; font-size: 8pt; vertical-align: top; }
table.main td.cr { font-size: 8pt; vertical-align: top; line-height: 1.35; }
.it { font-style: italic; font-weight: normal; font-size: 7.5pt; }
.sub { padding-left: 14px; }
.page2 { page-break-before: always; }
table.dt { width: 100%; border-collapse: collapse; }
table.dt td, table.dt th { border: 1px solid #000; padding: 5px 8px; font-size: 9pt; vertical-align: middle; line-height: 1.4; }
table.dt td.ck { width: 30px; text-align: center; }
table.dt th { font-weight: bold; text-align: left; background: #d9d9d9; }
.ft { margin-top: 50px; display: flex; justify-content: space-between; }
.ft div { text-align: center; font-size: 9.5pt; }
.ft .ln { border-top: 1px solid #000; min-width: 150px; margin-top: 60px; padding-top: 4px; }
@media print { body { margin:0; padding:5px; } }
</style></head><body>

<p class="hdr">OBRAZAC 1</p>
<p class="ttl">ZAHTJEV ZA REGISTRACIJU OBJEKTA ZA PROIZVODNJU, PRERADU I DISTRIBUCIJU HRANE</p>

<table class="main">
<tr><th colspan="2">A. PODACI O SUBJEKTU U POSLOVANJU HRANOM</th></tr>
<tr><td>Naziv subjekta (pravno lice/preduzetnik)</td><td class="val">${entry.subjekt_naziv||""}</td></tr>
<tr><td>Adresa /sjedi\u0161te subjekta (ulica i broj, mjesto)</td><td class="val">${entry.subjekt_adresa||""}</td></tr>
<tr><td>Odgovorno lice</td><td class="val">${entry.subjekt_odgovorno_lice||""}</td></tr>
<tr><td>Telefon / fax</td><td class="val">${entry.subjekt_telefon||""}</td></tr>
<tr><td>e-mail adresa</td><td class="val">${entry.subjekt_email||""}</td></tr>
<tr><th colspan="2">B. PODACI O OBJEKTU</th></tr>
<tr><td>Naziv objekta</td><td class="val">${entry.objekat_naziv||""}</td></tr>
<tr><td>Adresa objekta (ulica i broj, mjesto)</td><td class="val">${entry.objekat_adresa||""}</td></tr>
<tr><td>Povr\u0161ina objekta (m\u00b2)</td><td class="val">${entry.objekat_povrsina||""}</td></tr>
<tr><td>Broj radnih mjesta</td><td class="val">${entry.objekat_radna_mjesta||""}</td></tr>
<tr><td>Telefon / fax</td><td class="val">${entry.objekat_telefon||""}</td></tr>
<tr><td>e-mail adresa</td><td class="val">${entry.objekat_email||""}</td></tr>
<tr><th colspan="2">C. DJELATNOST <span style="font-weight:normal;font-size:7.5pt;">(odabrati i obilje\u017eiti osnovnu djelatnost)</span></th></tr>
<tr>
<td class="cl">GAZDINSTVA<br><span class="it">(na kojima se obavlja primarna proizvodnja hrane, sa pridru\u017eenim djelatnostima pakovanje, skladi\u0161tenje i transport)</span></td>
<td class="cr">
${cb(opcije,'Uzgoj živine i pakovanje jaja, kokoški, prepelica')} uzgoj \u017eivine i pakovanje jaja, koko\u0161ki, prepelica;<br>
${cb(opcije,'Proizvodnja, punjenje i/ili pakovanje meda i pčelinjih proizvoda')} proizvodnja, punjenje i/ili pakovanje meda i p\u010delinjih proizvoda;<br>
${cb(opcije,'Proizvodnja sirovog mlijeka - za prodaju/preradu na sopstvenom gazdinstvu')} proizvodnja sirovog mlijeka:<br>
<span class="sub">- za prodaju/preradu na sopstvenom gazdinstvu;</span><br>
<span class="sub">- radi predaje objektima za preradu mlijeka;</span><br>
${cb(opcije,'Sakupljanje sirovog mlijeka sa više gazdinstava')} sakupljanje sirovog mlijeka sa vi\u0161e gazdinstava za potrebe jednog objekta za preradu mlijeka
</td>
</tr>
<tr><td class="cl">SABIRNI CENTRI ZA MLIJEKO</td><td class="cr">${kat==='sabirni_centri'?'&#9745;':'&#9744;'}</td></tr>
<tr><td class="cl">UZGAJALI\u0160TA \u0160KOLJKI</td><td class="cr">${cb(opcije,'Morski')} morski &nbsp;&nbsp;&nbsp; ${cb(opcije,'Slatkovodni')} slatkovodni</td></tr>
<tr><td class="cl">RIBNJACI/UZGAJALI\u0160TA RIBE</td><td class="cr">${kat==='ribnjaci'?'&#9745;':'&#9744;'}</td></tr>
<tr><td class="cl">BROD <span class="it">(osim broda hladnja\u010de i broda fabrike)</span></td><td class="cr">${kat==='brod'?'&#9745;':'&#9744;'}</td></tr>
<tr><td class="cl">UZGAJALI\u0160TE PU\u017dEVA</td><td class="cr">${kat==='puzevi'?'&#9745;':'&#9744;'}</td></tr>
<tr>
<td class="cl">UGOSTITELJSTVO<br><span class="it">(priprema, uslu\u017eivanje i prodaja hrane)</span><br>neposredno snabdijevanje krajnjeg potro\u0161a\u010da</td>
<td class="cr">
${cb(opcije,'Hoteli - hotel, mali hotel, boutique hotel, garni hotel, apart hotel, pansion, motel, hostel')} Hoteli - hotel, mali hotel, boutique hotel, garni hotel, apart hotel, condo hotel, pansion, motel, hostel<br>
${cb(opcije,'Resort, hotel&resort, turističko naselje, turistička vila, etno selo, eko lodge')} Resort mixed-use resort, hotel&amp;resort, wild beauty resort, turisti\u010dko naselje, turisti\u010dka vila, etno selo, eko lodge<br>
${cb(opcije,'Kuća za iznajmljivanje, turistički apartman, soba za iznajmljivanje, gostionica, planinarski dom')} Ku\u0107a za iznajmljivanje, turisti\u010dki apartman i soba za iznajmljivanje, gostionica, planinarski dom, odmara\u0161i\u0161te, kamp, vila, rezidencijalna vila<br>
${cb(opcije,'Restoran, picerija, konoba, catering objekt, brza hrana')} Restoran, picerija, konoba, catering objekt, brza hrana<br>
${cb(opcije,'Caffe bar, kafana, kafić, kafeterija, disko klub, noćni klub, bistro, pivnica')} Grupa caffe barovi - caffe bar, kafana, kafi\u0107, kafeterija, disko klub, no\u0107ni klub, bistro, pivnica, kr\u010dma<br>
${cb(opcije,'Pekara, poslastičarnica, kantina, pečenjara, ćevabdžinica, buregdžinica')} Objekti jednostavnih usluga pekara, poslasti\u010darnica, kantina, pe\u010denjara, \u0107evab\u01f3inica, bureg\u01f3inica<br>
${cb(opcije,'Institucionalne kuhinje (obrazovne, socijalne, zdravstvene)')} Institucionalne kuhinje vaspitno obrazovne, socijalne i zdravstvene ustanove<br>
${cb(opcije,'Ostalo')} Ostalo
</td>
</tr>
<tr>
<td class="cl">SEKTOR TRGOVINA</td>
<td class="cr">
${cb(opcije,'Trgovina na malo')} Trgovina na malo<br>
${cb(opcije,'Veleprodaja i skladištenje hrane')} Veleprodaja i skladi\u0161tenje hrane (sa i/ili bez temperaturnog re\u017eima)<br>
${cb(opcije,'Apoteke i specijalizovane prodavnice')} Apoteke i specijalizovane prodavnice<br>
${cb(opcije,'Pijace / Tržnica')} Pijace/Tr\u017enica<br>
${cb(opcije,'Ostalo')} Ostalo
</td>
</tr>
<tr><td class="cl">PROIZVODNJA I PRODAJA HRANE NA POLJOPRIVREDNOM GAZDINSTVU</td><td class="cr">${kat==='gazdinstvo_prodaja'?'&#9745;':'&#9744;'} Proizvodnja i/ili prerada i/ili pakovanje malih koli\u010dina primarnih proizvoda \u017eivotinjskog i/ili ne\u017eivotinjskog porijekla proizvedena na porodi\u010dnom poljoprivrednom gazdinstvu</td></tr>
<tr><td class="cl">OSTALO</td><td class="cr">${kat==='ostalo'?'&#9745;':'&#9744;'}</td></tr>
</table>

<div class="page2">
<table class="dt">
<tr><th colspan="2">D. PRILO\u017dENA DOKUMENTACIJA</th></tr>
<tr><td class="ck">${cb(doks,'Rješenje/izvod iz Centralnog registra privrednih subjekata')}</td><td>rje\u0161enje/izvod iz Centralnog registra privrednih subjekata, odnosno Registra poljoprivrednih gazdinstava</td></tr>
<tr><td class="ck">${cb(doks,'Dokaz o svojini (list nepokretnosti ili ugovor o zakupu)')}</td><td>dokaz o svojini (list nepokretnosti ili ugovor o zakupu) za objekat, zemlji\u0161te-gazdinstvo</td></tr>
<tr><td class="ck">${cb(doks,'Skica objekta sa rasporedom prostorija i opreme')}</td><td>skica objekta sa rasporedom prostorija, opreme, prijema i kretanja sirovina i gotovih proizvoda</td></tr>
<tr><td class="ck">${cb(doks,'Opis tehnoloških postupaka u proizvodnji/preradi/pakovanju')}</td><td>opis tehnolo\u0161kih postupaka koji se sprovode u proizvodnji, obradi, preradi, pakovanju, skladi\u0161tenju, transportu i distribuciji (za poljoprivredna gazdinstva za proizvodnju i prodaju hrane)</td></tr>
<tr><td class="ck">${cb(doks,'Dozvola za marikulturu ili akvakulturu')}</td><td>dozvola za marikulturu ili akvakulturu (za uzgoj ribe, \u0161koljki i drugih proizvoda)</td></tr>
<tr><td class="ck">${cb(doks,'Dozvola za privredni ribolov')}</td><td>dozvola za privredni ribolov</td></tr>
<tr><td class="ck">${cb(doks,'Rješenje o korišćenju morskog dobra')}</td><td>rje\u0161enje o kori\u0161\u0107enju morskog dobra za objekte koji se nalaze u zoni morskog dobra</td></tr>
<tr><td class="ck">${cb(doks,'Dokaz o uplati administrativne takse')}</td><td>dokaz o uplati administrativne takse</td></tr>
</table>

<div class="ft">
<div style="text-align:left;font-size:9.5pt;">Mjesto i datum: ${grad}, ${formatDatePrint(entry.datum)||''}</div>
<div><p style="margin-top:50px;">M.P.</p></div>
<div><div class="ln">Potpis podnosioca</div></div>
</div>
</div>

</body></html>`;
  };

  const handlePrint = (entry) => printHTML(buildHtml(entry));
  const handleWord = (entry) => downloadWord(buildHtml(entry), `Registracija_hrane_${(entry.subjekt_naziv||'subjekt').replace(/\s+/g,'_')}.doc`);

  if (loading) return <div className="p-8 text-center text-slate-500">Ucitavanje...</div>;

  return (
    <div className="space-y-6" data-testid="reg-hrana-page">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-slate-900">Registracija objekta za hranu</h3>
          <p className="text-sm text-slate-600 mt-1">Obrazac 1 - Zahtjev za registraciju objekta za proizvodnju, preradu i distribuciju hrane</p>
        </div>
        <Button onClick={() => { setForm(emptyForm); setEditingId(null); setShowForm(true); }}
          className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="reg-hrana-add-btn">
          <Plus size={16} className="mr-2" /> Novi zahtjev
        </Button>
      </div>

      <Card className="p-4">
        <div className="relative max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Pretrazi..." className="pl-9 bg-white border-slate-300 text-sm" />
        </div>
      </Card>

      {filtered.length === 0 ? (
        <Card className="p-12 text-center"><p className="text-slate-500">Nema zahtjeva</p></Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Subjekt</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Objekat</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Djelatnost</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Datum</th>
                  <th className="px-4 py-3 text-xs font-semibold uppercase text-slate-500">Akcije</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(e => (
                  <tr key={e.id} className="border-b hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium">{e.subjekt_naziv}</td>
                    <td className="px-4 py-3">{e.objekat_naziv || '-'}</td>
                    <td className="px-4 py-3 text-xs">{DJELATNOSTI[e.djelatnost_kategorija]?.label || '-'}</td>
                    <td className="px-4 py-3 text-xs">{formatDatePrint(e.datum) || '-'}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button onClick={() => setShowDetail(e)} className="p-2 hover:bg-blue-100 rounded"><Eye size={16} className="text-blue-600" /></button>
                        <button onClick={() => handlePrint(e)} className="p-2 hover:bg-green-100 rounded"><Printer size={16} className="text-green-600" /></button>
                        <button onClick={() => handleWord(e)} className="p-2 hover:bg-indigo-100 rounded" title="Word dokument"><FileDoc size={16} className="text-indigo-600" /></button>
                        <button onClick={() => handleEdit(e)} className="p-2 hover:bg-amber-100 rounded"><Pencil size={16} className="text-amber-600" /></button>
                        <button onClick={() => handleDelete(e.id)} className="p-2 hover:bg-red-100 rounded"><Trash size={16} className="text-red-600" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="reg-hrana-form">
          <DialogHeader><DialogTitle>{editingId ? 'Izmijeni zahtjev' : 'Novi zahtjev - Obrazac 1'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Client select */}
            <div className="space-y-1">
              <Label className="text-xs text-slate-500 font-semibold">Klijent (auto-popunjavanje)</Label>
              <select value={form.client_id} onChange={e => handleClientChange(e.target.value)}
                className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm h-10">
                <option value="">-- Izaberite klijenta --</option>
                {sortedClients.map(c => <option key={c.id} value={c.id}>{c.skraceni_naziv || c.naziv_firme}</option>)}
              </select>
            </div>

            <h4 className="text-sm font-semibold text-slate-700 border-b pb-1">A. Podaci o subjektu</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1"><Label className="text-xs text-slate-500">Naziv subjekta *</Label>
                <Input value={form.subjekt_naziv} onChange={e => setForm({...form, subjekt_naziv: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
              <div className="col-span-2 space-y-1"><Label className="text-xs text-slate-500">Adresa / sjedište</Label>
                <Input value={form.subjekt_adresa} onChange={e => setForm({...form, subjekt_adresa: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Odgovorno lice</Label>
                <Input value={form.subjekt_odgovorno_lice} onChange={e => setForm({...form, subjekt_odgovorno_lice: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Telefon / fax</Label>
                <Input value={form.subjekt_telefon} onChange={e => setForm({...form, subjekt_telefon: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
              <div className="col-span-2 space-y-1"><Label className="text-xs text-slate-500">E-mail</Label>
                <Input value={form.subjekt_email} onChange={e => setForm({...form, subjekt_email: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
            </div>

            <h4 className="text-sm font-semibold text-slate-700 border-b pb-1">B. Podaci o objektu</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1"><Label className="text-xs text-slate-500">Naziv objekta</Label>
                <Input value={form.objekat_naziv} onChange={e => setForm({...form, objekat_naziv: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
              <div className="col-span-2 space-y-1"><Label className="text-xs text-slate-500">Adresa objekta</Label>
                <Input value={form.objekat_adresa} onChange={e => setForm({...form, objekat_adresa: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Površina (m²)</Label>
                <Input value={form.objekat_povrsina} onChange={e => setForm({...form, objekat_povrsina: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Broj radnih mjesta</Label>
                <Input value={form.objekat_radna_mjesta} onChange={e => setForm({...form, objekat_radna_mjesta: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Telefon / fax</Label>
                <Input value={form.objekat_telefon} onChange={e => setForm({...form, objekat_telefon: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">E-mail</Label>
                <Input value={form.objekat_email} onChange={e => setForm({...form, objekat_email: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
            </div>

            <h4 className="text-sm font-semibold text-slate-700 border-b pb-1">C. Djelatnost</h4>
            <div className="space-y-2">
              <select value={form.djelatnost_kategorija} onChange={e => setForm({...form, djelatnost_kategorija: e.target.value, djelatnost_opcije: []})}
                className="flex w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm h-10">
                <option value="">-- Izaberite djelatnost --</option>
                {Object.entries(DJELATNOSTI).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              {form.djelatnost_kategorija && DJELATNOSTI[form.djelatnost_kategorija]?.options.length > 0 && (
                <div className="p-3 bg-slate-50 rounded border space-y-1.5 max-h-48 overflow-y-auto">
                  {DJELATNOSTI[form.djelatnost_kategorija].options.map(opt => (
                    <label key={opt} className="flex items-start gap-2 text-xs cursor-pointer hover:bg-slate-100 p-1 rounded">
                      <input type="checkbox" checked={form.djelatnost_opcije.includes(opt)}
                        onChange={() => toggleDjelatnostOpcija(opt)} className="mt-0.5" />
                      <span>{opt}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <h4 className="text-sm font-semibold text-slate-700 border-b pb-1">D. Priložena dokumentacija</h4>
            <div className="p-3 bg-slate-50 rounded border space-y-1.5">
              {DOKUMENTACIJA.map(doc => (
                <label key={doc} className="flex items-start gap-2 text-xs cursor-pointer hover:bg-slate-100 p-1 rounded">
                  <input type="checkbox" checked={form.dokumentacija.includes(doc)}
                    onChange={() => toggleDokumentacija(doc)} className="mt-0.5" />
                  <span>{doc}</span>
                </label>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs text-slate-500">Datum</Label>
                <DateInput value={form.datum} onChange={e => setForm({...form, datum: e.target.value})} className="border-slate-300 text-sm" /></div>
              <div className="space-y-1"><Label className="text-xs text-slate-500">Napomena</Label>
                <Input value={form.napomena} onChange={e => setForm({...form, napomena: e.target.value})} className="bg-white border-slate-300 text-sm" /></div>
            </div>

            <div className="flex gap-3 justify-end pt-2 border-t">
              <Button variant="outline" onClick={() => setShowForm(false)}>Odustani</Button>
              <Button onClick={handleSubmit} className="bg-blue-600 hover:bg-blue-700 text-white">
                {editingId ? 'Sacuvaj' : 'Kreiraj'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {showDetail && (
            <>
              <DialogHeader><DialogTitle>Obrazac 1 - {showDetail.subjekt_naziv}</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2 text-sm">
                <div className="flex justify-end gap-2">
                  <Button size="sm" onClick={() => handlePrint(showDetail)} className="bg-green-600 hover:bg-green-700 text-white text-xs">
                    <Printer size={14} className="mr-1" /> Stampaj Obrazac 1
                  </Button>
                  <Button size="sm" onClick={() => handleWord(showDetail)} className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs">
                    <FileDoc size={14} className="mr-1" /> Word
                  </Button>
                </div>
                <h4 className="font-semibold text-slate-700 border-b pb-1">A. Subjekt</h4>
                <div className="grid grid-cols-2 gap-2">
                  {[['Naziv', showDetail.subjekt_naziv], ['Adresa', showDetail.subjekt_adresa], ['Odgovorno lice', showDetail.subjekt_odgovorno_lice], ['Telefon', showDetail.subjekt_telefon], ['Email', showDetail.subjekt_email]].map(([l,v]) => (
                    <div key={l} className="p-2 bg-slate-50 rounded border"><span className="text-xs text-slate-500">{l}</span><p className="font-medium">{v || '-'}</p></div>
                  ))}
                </div>
                <h4 className="font-semibold text-slate-700 border-b pb-1">B. Objekat</h4>
                <div className="grid grid-cols-2 gap-2">
                  {[['Naziv', showDetail.objekat_naziv], ['Adresa', showDetail.objekat_adresa], ['Površina', showDetail.objekat_povrsina], ['Radna mjesta', showDetail.objekat_radna_mjesta]].map(([l,v]) => (
                    <div key={l} className="p-2 bg-slate-50 rounded border"><span className="text-xs text-slate-500">{l}</span><p className="font-medium">{v || '-'}</p></div>
                  ))}
                </div>
                <h4 className="font-semibold text-slate-700 border-b pb-1">C. Djelatnost</h4>
                <p className="font-medium">{DJELATNOSTI[showDetail.djelatnost_kategorija]?.label || '-'}</p>
                {(showDetail.djelatnost_opcije || []).length > 0 && (
                  <ul className="list-disc list-inside text-xs space-y-1">
                    {showDetail.djelatnost_opcije.map(o => <li key={o}>{o}</li>)}
                  </ul>
                )}
                <h4 className="font-semibold text-slate-700 border-b pb-1">D. Dokumentacija</h4>
                <ul className="text-xs space-y-1">
                  {(showDetail.dokumentacija || []).map(d => <li key={d}>&#9745; {d}</li>)}
                </ul>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
